import { omitBy } from "@common/helpers/objects";
import { AccrualSuggestionBucket } from "@common/types/accrualSuggestionBucket";
import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { ViewDensity } from "@common/types/views/views";
import { TransactionsFilters } from "@components/transactions/filters/lib";
import { NoteCellVariant } from "@components/transactions/views/noteCellVariants";

export const AccrualReviewTab = {
    TO_REVIEW: "toReview",
    REVIEWED: "reviewed",
} as const;

export type AccrualReviewTab =
    (typeof AccrualReviewTab)[keyof typeof AccrualReviewTab];

export const isAccrualReviewTab = (
    value: string | null | undefined,
): value is AccrualReviewTab =>
    value === AccrualReviewTab.TO_REVIEW || value === AccrualReviewTab.REVIEWED;

/** The tab the current filters show: Reviewed once they ask for reviewed rows. */
export const getAccrualReviewTab = (
    filters: TransactionsFilters,
): AccrualReviewTab =>
    filters.isAccrualReviewed === true
        ? AccrualReviewTab.REVIEWED
        : AccrualReviewTab.TO_REVIEW;

/** The filter patch that moves the table onto a tab's transactions. */
export const getAccrualReviewTabFilters = (
    tab: AccrualReviewTab,
): Partial<TransactionsFilters> =>
    tab === AccrualReviewTab.REVIEWED
        ? { isNotAccrualReviewed: undefined, isAccrualReviewed: true }
        : { isNotAccrualReviewed: true, isAccrualReviewed: undefined };

/** Applies a filter patch; null or undefined values remove the filter. */
export const mergeTaskFilters = (
    current: TransactionsFilters,
    patch: Partial<TransactionsFilters>,
): TransactionsFilters =>
    omitBy(
        { ...current, ...patch },
        (value) => value === null || value === undefined,
    );

/**
 * Buckets whose tasks never display the suggested-match column: their
 * transactions never have an `accrual_match_suggestion` row (NONE by
 * definition; RULE_SUGGESTED because the suggestion comes from a rule).
 * Mirrors the backend so legacy tasks still render the right columns.
 */
const BUCKETS_WITHOUT_MATCH_COLUMN: ReadonlySet<AccrualSuggestionBucket> =
    new Set([
        AccrualSuggestionBucket.RULE_SUGGESTED,
        AccrualSuggestionBucket.NONE,
    ]);

const shouldHideMatchColumn = (view: TransactionView): boolean => {
    const buckets = view.filters?.accrualSuggestionBuckets ?? [];
    return (
        buckets.length > 0 &&
        buckets.every((bucket) => BUCKETS_WITHOUT_MATCH_COLUMN.has(bucket))
    );
};

const withoutMatchColumn = (view: TransactionView): TransactionView => ({
    ...view,
    columns: view.columns.filter(
        (column) => column.id !== TransactionColumn.ACCRUAL_MATCH,
    ),
});

const MEMO_INSERT_BEFORE_COLUMNS = [
    TransactionColumn.ACCRUAL_MATCH,
    TransactionColumn.ACCRUAL_REVIEW_LEDGER,
    TransactionColumn.ACCRUAL_ACTIONS,
];

/**
 * Adds the inline Memo column right before the first accrual column (match,
 * else ledger, else actions, else at the end). Without a match column it is
 * pinned with the ledger so it never scrolls out of the narrow task body; with
 * one it scrolls, since the pinned match, ledger and actions already fill most
 * of the width. Views that already have Memo are returned as is, so customized
 * views keep their columns.
 */
export const withMemoColumn = (view: TransactionView): TransactionView => {
    if (view.columns.some((column) => column.id === TransactionColumn.NOTE)) {
        return view;
    }
    const hasMatchColumn = view.columns.some(
        (column) => column.id === TransactionColumn.ACCRUAL_MATCH,
    );
    const memoColumn = {
        id: TransactionColumn.NOTE,
        options: {
            ...(hasMatchColumn ? {} : { pin: "right" }),
            customVariants: { variant: NoteCellVariant.INLINE },
        },
    } satisfies TransactionView["columns"][number];
    const anchorIndex = MEMO_INSERT_BEFORE_COLUMNS.map((columnId) =>
        view.columns.findIndex((column) => column.id === columnId),
    ).find((index) => index !== -1);
    const insertIndex = anchorIndex ?? view.columns.length;
    return {
        ...view,
        columns: [
            ...view.columns.slice(0, insertIndex),
            memoColumn,
            ...view.columns.slice(insertIndex),
        ],
    };
};

/** The task's saved view as the table's default: bucket columns plus Memo. */
export const getAccrualReviewTaskDefaultView = (
    view: TransactionView,
): TransactionView =>
    withMemoColumn(
        shouldHideMatchColumn(view) ? withoutMatchColumn(view) : view,
    );

/**
 * The view the table renders. To review lets the user select and confirm
 * rows; Reviewed is read-only (cells keep only Memo editable). Rows default
 * to the medium density the task design uses.
 */
export const getAccrualReviewTableView = ({
    view,
    tab,
}: {
    view: TransactionView;
    tab: AccrualReviewTab;
}): TransactionView => {
    const options = {
        ...view.options,
        density: view.options?.density ?? ViewDensity.MD,
    };
    return {
        ...view,
        options:
            tab === AccrualReviewTab.REVIEWED
                ? {
                      ...options,
                      readonly: true,
                      bulkActions: false,
                      selection: false,
                  }
                : { ...options, bulkActions: true, selection: true },
    };
};

/**
 * The task's progress for the status line: "Reviewed N of M", or null until
 * both counts load or when the task covers nothing.
 */
export const getReviewStatusLabel = ({
    total,
    remainingCount,
}: {
    total: number | undefined;
    remainingCount: number | undefined;
}): string | null => {
    if (total === undefined || remainingCount === undefined || total === 0) {
        return null;
    }
    // The two counts refetch separately and can briefly disagree.
    const reviewedCount = Math.max(0, total - remainingCount);
    return reviewedCount >= total
        ? "All reviewed"
        : `Reviewed ${reviewedCount} of ${total}`;
};
